import React, { useRef } from 'react';
import { Popover, Icon, Switch, Tag, Dropdown, Menu } from 'antd';
import { useDrag, useDrop, DropTargetMonitor } from 'react-dnd';
import { XYCoord } from 'dnd-core';

import { IReportViewProps, IReportQueryViewProps } from '@duoke/tools/umd/report';
import styles from './index.less';

const { CheckableTag } = Tag;
const VIEW_ITEM = 'viewItem';

export interface ICardProps {
  index: number;
  disabled: boolean;
  source: IReportViewProps;
  value: IReportQueryViewProps;
  onClose: (e: boolean) => void;
  onChange?: (c: IReportQueryViewProps) => void;
  moveView: (dragIndex: number, hoverIndex: number) => void;
}

interface IDragItem {
  type: string;
  index: number;
  source: IReportViewProps;
  value: IReportQueryViewProps;
}

const Card: React.FC<ICardProps> = ({
  index,
  value,
  source = {},
  onClose,
  moveView,
  onChange,
  disabled,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [, drop] = useDrop({
    accept: VIEW_ITEM,
    hover(item: IDragItem, monitor: DropTargetMonitor) {
      if (!ref.current || value.fixed || item.value.fixed) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current!.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Time to actually perform the action
      moveView(dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    item: { type: VIEW_ITEM, source, value, index },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const opacity = isDragging ? 0 : 1;

  drag(drop(ref));

  return (
    <div ref={ref} style={{ opacity, cursor: value.fixed ? 'initial' : 'move' }}>
      <div className={styles['view-item']}>
        <div>
          {source.description ? (
            <Popover content={source.description || '暂无描述'}>
              <Icon type="question-circle" className={styles['view-desc']} />
            </Popover>
          ) : (
            <span className={styles['view-desc']} />
          )}
          <span className={styles['text-ellipse']}>{source.name}</span>
        </div>
        <div>
          {value.fixed ? (
            <CheckableTag
              checked
              className={styles['tag-checked']}
              onChange={() => onChange && onChange({ ...value, fixed: '' })}
            >
              已钉住
            </CheckableTag>
          ) : (
            <Dropdown
              trigger={['click']}
              overlay={
                <Menu
                  onClick={({ key }: { key: 'left' | 'right' }) =>
                    onChange && onChange({ ...value, fixed: key })
                  }
                >
                  <Menu.Item key="left"> 钉在首位 </Menu.Item>
                  <Menu.Item key="right"> 钉在末尾 </Menu.Item>
                </Menu>
              }
            >
              <Tag className={styles['tag-unchecked']}>钉住</Tag>
            </Dropdown>
          )}
          <Switch size="small" defaultChecked disabled={disabled} onChange={onClose} />
        </div>
      </div>
    </div>
  );
};

export default Card;
